/**
 * Lab401: Review cards grid.
 * Fetches reviews from the Judge.me API, optionally filtered by product ID.
 * Context-aware: if data-product-id is set, filters by product.
 * Otherwise shows all reviews.
 *
 * Security: All content inserted via textContent or safe DOM methods.
 */
(function () {
  'use strict';

  var API = 'https://shopify-judgeme-review-feed.lab401.workers.dev/reviews';
  var container = document.querySelector('[data-product-reviews]');
  if (!container) return;

  var productId = container.getAttribute('data-product-id');
  var maxReviews = parseInt(container.getAttribute('data-max-reviews'), 10) || 20;

  /* ── Parse time labels from Liquid-rendered JSON ─────────── */
  var labels = {};
  try {
    var el = document.querySelector('[data-product-review-labels]');
    if (el) labels = JSON.parse(el.textContent);
  } catch (e) { /* defaults */ }

  var lang = (labels.lang || document.documentElement.lang || 'en').toLowerCase().split('-')[0];
  var verifiedText = labels.verified || 'Verifiable Review from';

  /* ── "Time ago" formatter ──────────────────────────────────── */
  var prefixLangs = { fr: true, es: true, de: true };

  function timeAgo(isoDate) {
    var now = Date.now();
    var then = new Date(isoDate).getTime();
    var diffSec = Math.max(0, Math.floor((now - then) / 1000));
    var diffMin = Math.floor(diffSec / 60);
    var diffHr  = Math.floor(diffMin / 60);
    var diffDay = Math.floor(diffHr / 24);
    var diffWeek = Math.floor(diffDay / 7);
    var diffMonth = Math.floor(diffDay / 30);
    var diffYear = Math.floor(diffDay / 365);

    var ago = labels.ago || 'ago';
    var n, unit;

    if (diffYear >= 1) {
      n = diffYear; unit = diffYear === 1 ? (labels.year || 'year') : (labels.years || 'years');
    } else if (diffMonth >= 1) {
      n = diffMonth; unit = diffMonth === 1 ? (labels.month || 'month') : (labels.months || 'months');
    } else if (diffDay >= 14) {
      n = diffWeek; unit = diffWeek === 1 ? (labels.week || 'week') : (labels.weeks || 'weeks');
    } else if (diffDay >= 7) {
      return labels.last_week || 'last week';
    } else if (diffDay >= 2) {
      n = diffDay; unit = labels.days || 'days';
    } else if (diffDay >= 1) {
      return labels.yesterday || 'yesterday';
    } else if (diffHr >= 1) {
      n = diffHr; unit = diffHr === 1 ? (labels.hour || 'hour') : (labels.hours || 'hours');
    } else if (diffMin >= 1) {
      n = diffMin; unit = diffMin === 1 ? (labels.minute || 'minute') : (labels.minutes || 'minutes');
    } else {
      n = diffSec; unit = diffSec === 1 ? (labels.second || 'second') : (labels.seconds || 'seconds');
    }

    return prefixLangs[lang] ? (ago + ' ' + n + ' ' + unit) : (n + ' ' + unit + ' ' + ago);
  }

  /* ── Star string ───────────────────────────────────────────── */
  function stars(rating) {
    var s = '';
    for (var i = 0; i < 5; i++) s += (i < rating) ? '\u2605' : '\u2606';
    return s;
  }

  /* ── Mask PII: "First Last" → "First L." ───────────────────── */
  function maskName(name) {
    if (!name) return '';
    var parts = name.trim().split(/\s+/);
    if (parts.length < 2) return parts[0];
    return parts[0] + ' ' + parts[parts.length - 1].charAt(0).toUpperCase() + '.';
  }

  /* ── Build Shop SVG pill using safe DOM methods ────────────── */
  function buildShopPill() {
    var pill = document.createElement('a');
    pill.className = 'pr-card__shop-pill';
    pill.href = 'https://shop.app/m/lab401';
    pill.target = '_blank';
    pill.rel = 'noopener';

    var template = document.querySelector('[data-pr-shop-svg]');
    if (template) {
      pill.appendChild(template.content.cloneNode(true));
    }
    return pill;
  }

  /* ── Build a single review card ────────────────────────────── */
  function buildCard(review) {
    var card = document.createElement('div');
    card.className = 'pr-card';

    /* Top row: stars + date */
    var top = document.createElement('div');
    top.className = 'pr-card__top';

    var starsEl = document.createElement('span');
    starsEl.className = 'pr-card__stars';
    starsEl.textContent = stars(review.rating);
    top.appendChild(starsEl);

    var dateEl = document.createElement('span');
    dateEl.className = 'pr-card__date';
    dateEl.textContent = timeAgo(review.created_at);
    top.appendChild(dateEl);

    card.appendChild(top);

    /* Body text */
    var body = document.createElement('p');
    body.className = 'pr-card__body';
    body.textContent = review.body || '';
    card.appendChild(body);

    /* Footer: author + verified */
    var footer = document.createElement('div');
    footer.className = 'pr-card__footer';

    var author = document.createElement('span');
    author.className = 'pr-card__author';
    author.textContent = '- ' + maskName((review.reviewer && review.reviewer.name) || '');
    footer.appendChild(author);

    var verified = document.createElement('span');
    verified.className = 'pr-card__verified';

    var vLink = document.createElement('a');
    vLink.className = 'pr-card__verified-link';
    vLink.href = '/pages/lab401-customer-reviews-testimonals-policy';
    vLink.textContent = verifiedText;
    verified.appendChild(vLink);

    verified.appendChild(buildShopPill());

    footer.appendChild(verified);
    card.appendChild(footer);

    return card;
  }

  /* ── Fetch and render ──────────────────────────────────────── */
  var url = API + '?lang=' + encodeURIComponent(lang);
  if (productId) {
    url += '&product_id=' + encodeURIComponent(productId);
  }

  fetch(url)
    .then(function (res) {
      if (!res.ok) throw new Error(res.status);
      return res.json();
    })
    .then(function (data) {
      if (!data.reviews || !data.reviews.length) {
        /* No reviews — hide entire section including heading */
        var section = container.closest('.pr-section');
        if (section) section.style.display = 'none';
        return;
      }

      var reviews = data.reviews.slice(0, maxReviews);

      for (var i = 0; i < reviews.length; i++) {
        var item = document.createElement('div');
        item.className = 'pr-grid__item';
        item.appendChild(buildCard(reviews[i]));
        container.appendChild(item);
      }
    })
    .catch(function () {
      /* Silent failure — hide entire section */
      var section = container.closest('.pr-section');
      if (section) section.style.display = 'none';
    });
})();
